import { cva } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, type ReactNode, type Ref } from 'react';
import { cn } from '@/lib/cn';

/**
 * A button whose visible content is a glyph (`FE-04.10`).
 *
 * **`label` is required and has no default.** All three Stage 2 returns asked
 * for exactly this, unprompted and in nearly the same words — the strongest
 * convergence in the chunk. It becomes the accessible name; the glyph is
 * decorative. An icon whose meaning lives only in the accessibility tree is
 * half a control, so callers are expected to wrap this in `Tooltip` where the
 * meaning is not obvious — but the tooltip is a *supplement*, never the name.
 *
 * **`badge` is a slot, not a count.** The caller passes `<Badge count={3} />`.
 * The badge is `aria-hidden` — decorative by position — so the count must also
 * reach `label` ("Notifications, 3 unread"), or it is eyes-only. This component
 * cannot enforce that; the test file records the expectation.
 */

const iconButtonVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center rounded-card',
    'transition-colors',
    'disabled:cursor-not-allowed disabled:opacity-60',
    'aria-busy:cursor-progress',
  ],
  {
    variants: {
      variant: {
        // Text-weight on a theme surface. Inherits the global focus ring.
        quiet: 'text-ink-muted hover:bg-surface-raised hover:text-ink',
        // Sits on the scrim or on a photo background, where the accent ring
        // has no guaranteed contrast. This ADDS a ring colour; it never
        // removes the ring.
        'on-scrim': [
          'text-board-ink-light hover:bg-overlay',
          'focus-visible:outline-board-ink-light',
        ],
      },
      size: {
        sm: 'size-7',
        md: 'size-9',
      },
    },
    defaultVariants: { variant: 'quiet', size: 'md' },
  },
);

export interface IconButtonProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'aria-label' | 'ref'
> {
  /** Required. The accessible name — there is no default and no fallback. */
  label: string;
  size?: 'sm' | 'md';
  variant?: 'quiet' | 'on-scrim';
  /**
   * A slot for `<Badge />`. Rendered `aria-hidden`; put the same information in
   * `label`.
   */
  badge?: ReactNode;
  /** In flight. Sets `aria-busy` and swaps the glyph for a spinner. */
  busy?: boolean;
  /** The glyph. */
  children?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export function IconButton({
  label,
  size,
  variant,
  badge,
  busy,
  children,
  className,
  type,
  ...rest
}: IconButtonProps) {
  return (
    <button
      // Default to `button`. A bare <button> inside a form submits it, which is
      // never what an icon affordance means.
      type={type ?? 'button'}
      aria-label={label}
      aria-busy={busy === true ? true : undefined}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...rest}
    >
      <span aria-hidden="true" className="flex items-center justify-center">
        {busy === true ? <Spinner /> : children}
      </span>
      {badge !== undefined && (
        <span aria-hidden="true" className="pointer-events-none absolute -right-1 -top-1">
          {badge}
        </span>
      )}
    </button>
  );
}

/**
 * Private to this file — `Button` carries its own copy rather than importing
 * one from here, so `TextField → IconButton` does not drag in `Button`'s
 * `Tooltip` dependency.
 */
function Spinner() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-4 motion-safe:animate-spin"
      focusable="false"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.3"
      />
      <path
        d="M8 2a6 6 0 0 1 6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
